/* eslint-disable @typescript-eslint/no-explicit-any */

import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import type { IModularBenefitContact } from '../../../types/modular-benefit';
import type { AcceptedContact } from '@ben/shared/utils/contacts';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import AddNewPersonBlock from '../add-new-person-block/add-new-person-block';
import ConfigWindowClosedBanner from '../config-window-closed-banner/config-window-closed-banner';
import EditPersonButton from '../edit-person-modal/edit-person-button';
import type { ISliderValue } from '../multi-select-options/multi-select-options';
import MultiSelectOptions from '../multi-select-options/multi-select-options';
import { useNavigateProfileDependents } from '../use-navigate-profile-dependents';

const Beneficiaries = ({
  values,
  contacts,
  onChange,
  disabled,
  id,
  acceptedContactTypes,
  configWindow,
  isConditionalApprovalTriggered,
  handleValidationErrorCallback,
  benefit,
  setNewContactId,
  ...rest
}: any) => {
  const { t } = useTranslation();
  const [assignNobody, setAssignNobody] = useState(
    !values || values.length === 0
  );
  const [sliderValues, setSliderValues] = useState<ISliderValue[]>(values);

  const canAmendPerson =
    benefit?.enrolment?.configWindowOpen ||
    !benefit?.enrolment?.lockedWindowOpen;

  const { navigateAddNewDependent, navigateEditDependent } =
    useNavigateProfileDependents({
      acceptedContactTypes: acceptedContactTypes as AcceptedContact[],
    });

  useEffect(() => {
    if (!assignNobody && values?.length === 0) {
      handleValidationErrorCallback({
        hasError: true,
        message: [t('contacts.beneficiariesAllocationsError')],
      });
      return;
    }
    if (!values || values.length === 0) {
      handleValidationErrorCallback({
        hasError: false,
      });
      return;
    }
    const percentagesComplete =
      values.reduce(
        (total: number, value: any) => total + Number(value.percentage),
        0
      ) === 100;

    if (!percentagesComplete) {
      handleValidationErrorCallback({
        hasError: true,
        message: [t('contacts.beneficiariesAllocationsError')],
      });
      return;
    }
    // passing handleValidationErrorCallback as a dependency causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, t, assignNobody]);

  const handleAssignNobodyChange = useCallback(
    (newValues: string[]) => {
      if (newValues && newValues.length > 0) {
        setAssignNobody(true);
        // but we want to keep the current values in the slider
        onChange([]);
        return;
      }
      setAssignNobody(false);
      onChange(sliderValues);
    },
    [onChange, sliderValues]
  );

  const filteredContacts = contacts?.filter(
    ({ disabled }: { disabled: boolean }) => !disabled
  );

  useEffect(() => {
    setSliderValues((currentSliderValues) =>
      filterSliderValuesByContacts(currentSliderValues, filteredContacts)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);
  const handleChange = useCallback(
    (newValues: ISliderValue[], id?: string) => {
      setAssignNobody(false);
      // make percentage 100 if single person available and being switched on
      if (
        filteredContacts?.length === 1 &&
        newValues.length === 1 &&
        (!sliderValues || sliderValues?.length === 0)
      ) {
        setSliderValues([{ ...newValues[0], percentage: 100 }]);
        onChange([{ ...newValues[0], percentage: 100 }]);
        return;
      }
      const newValuesTotal = newValues.reduce(
        (total, value: any) => total + Number(value.percentage),
        0
      );
      if (newValuesTotal <= 100) {
        setSliderValues(newValues);
        onChange(filterZeroSliderValues(newValues));
        return;
      }
      const difference = newValuesTotal - 100;
      const adjustedValues = newValues.map((sliderValue) => {
        if (sliderValue.id === id) {
          return {
            ...sliderValue,
            percentage: Number(sliderValue.percentage) - difference,
          };
        }
        return sliderValue;
      });

      setSliderValues(adjustedValues);
      onChange(filterZeroSliderValues(adjustedValues));
    },

    [onChange, sliderValues, filteredContacts]
  );
  const nobodyStep = () => {
    const { benefitId } = rest;
    return (
      <MultiSelectOptions
        title={t('benefit.beneficiariesAssignNobody') ?? 'Assign nobody'}
        showTitle={false}
        hasSlider={false}
        disabled={disabled}
        options={[
          { label: t('benefit.beneficiariesAssignNobody'), key: 'nobody' },
        ]}
        onChange={handleAssignNobodyChange}
        values={assignNobody ? ['nobody'] : []}
        noGap
        benefitId={benefitId}
      />
    );
  };
  if (filteredContacts?.length === 0) {
    return (
      <NobodyAddedContainer>
        <AddNewPersonBlock
          navigateAddNewDependent={navigateAddNewDependent}
          description={t('addNewPersonBlock.beneficiariesMessage')}
        />
        {nobodyStep()}
      </NobodyAddedContainer>
    );
  }

  const options = filteredContacts?.map((contact: any) => {
    return {
      label: `${contact.firstName} ${contact.lastName}`,
      key: contact.id,
      description: contact?.contactType,
      subComponents: contact.subComponents,
    };
  });

  return (
    <Container>
      <MultiSelectOptions
        {...rest}
        hasSlider
        disabled={disabled}
        options={options}
        sliderValues={sliderValues}
        showSlider={true}
        onChangeSlider={handleChange}
        noGap
        disabledSliders={assignNobody}
        key={id}
        configWindow={configWindow}
        isConditionalApprovalTriggered={isConditionalApprovalTriggered}
      />
      {nobodyStep()}
      {configWindow?.isOpen || canAmendPerson ? (
        <ButtonsWrapper>
          <StyledButton
            variant={ButtonVariant.primary}
            label={t('addNewPersonBlock.addNewPerson')}
            onClick={navigateAddNewDependent}
            minWidth="214px"
          />
          <EditPersonButton
            navigateEditDependent={navigateEditDependent}
            contacts={filteredContacts}
            isBeneficiaryComponent={true}
          />
        </ButtonsWrapper>
      ) : (
        <ConfigWindowClosedBanner />
      )}
    </Container>
  );
};

export default Beneficiaries;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const ButtonsWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-start;
    gap: ${theme.spacing(2)};
  `}
`;

const NobodyAddedContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;

const filterZeroSliderValues = (sliderValues: ISliderValue[]) => {
  if (!sliderValues) return sliderValues;
  return sliderValues.filter(({ percentage }) => Number(percentage) > 0);
};

const filterSliderValuesByContacts = (
  currentSliderValues: ISliderValue[],
  filteredContacts: IModularBenefitContact[]
) => {
  return currentSliderValues?.filter(({ id }) =>
    filteredContacts?.find(
      ({ id: contactId }) => String(contactId) === String(id)
    )
  );
};
